import type { z } from "zod";
import type { QueryRoute, MutationRoute, HandlerParams } from "./types.ts";

// ---------------------------------------------------------------------------
// defineQuery / defineMutation
//
// These are the only functions edge function authors touch.
// Input type inferred from Zod schema. Output type inferred from handler.
// ---------------------------------------------------------------------------

/**
 * Define a query (read) edge function.
 *
 * @example
 * ```ts
 * export const fn = defineQuery({
 *   input: z.object({ id: z.string().uuid() }),
 *   handler: async ({ input }) => {
 *     return db.selectFrom('stores')
 *       .where('id', '=', input.id)
 *       .executeTakeFirst()
 *   },
 * })
 * ```
 */
export function defineQuery<S extends z.ZodType, O>(config: {
	input: S;
	handler: (params: HandlerParams<z.infer<S>>) => Promise<O>;
}): QueryRoute<z.infer<S>, Awaited<O>> {
	return {
		_type: "query" as const,
		_input: undefined as any,
		_output: undefined as any,
		input: config.input,
		handler: config.handler as any,
	};
}

/**
 * Define a mutation (write) edge function.
 *
 * @example
 * ```ts
 * export const fn = defineMutation({
 *   input: z.object({ name: z.string(), address: z.string() }),
 *   handler: async ({ input }) => {
 *     return db.insertInto('stores').values(input).returningAll().executeTakeFirst()
 *   },
 * })
 * ```
 */
export function defineMutation<S extends z.ZodType, O>(config: {
	input: S;
	handler: (params: HandlerParams<z.infer<S>>) => Promise<O>;
}): MutationRoute<z.infer<S>, Awaited<O>> {
	return {
		_type: "mutation" as const,
		_input: undefined as any,
		_output: undefined as any,
		input: config.input,
		handler: config.handler as any,
	};
}
